<script setup lang="ts">
import { SbList } from "@unio/saltbox-ui"
import { computed, reactive, shallowRef } from "vue"

import OutingVerificationRow from "@/components/outings/OutingVerificationRow.vue"
import { ICON } from "@/lib/icons"
import type { OutingItemSummary } from "@/types/models"

type VerificationResult = { present: boolean } | { quantity: number }
type ViewMode = "container" | "item"

const props = defineProps<{
  items: OutingItemSummary[]
  outingStatus: "draft" | "out" | "closed"
  processingItemId?: string
}>()

const emit = defineEmits<{
  checkIn: [outingItemId: string]
  pack: [item: OutingItemSummary, packed: boolean]
  remove: [outingItemId: string]
  verify: [item: OutingItemSummary, result: VerificationResult]
}>()

const activeView = shallowRef<ViewMode>("container")
const expandedContainers = reactive(new Set<string>())
const viewItems = [
  { label: "By container", value: "container" },
  { label: "By item", value: "item" },
]

const phase = computed(() => (props.outingStatus === "draft" ? "gather" : "return"))
const containers = computed(() => props.items.filter((item) => item.is_container))
const itemRows = computed(() => props.items.filter((item) => !item.is_container))
const looseItems = computed(() => itemRows.value.filter((item) => item.home_container_id === null))
const containerGroups = computed(() =>
  containers.value.map((container) => ({
    container,
    contents: props.items.filter(
      (item) => item.id !== container.id && item.home_container_id === container.item_id,
    ),
  })),
)

function toggleContainer(containerId: string) {
  if (expandedContainers.has(containerId)) {
    expandedContainers.delete(containerId)
  } else {
    expandedContainers.add(containerId)
  }
}

function checkedCount(contents: OutingItemSummary[]) {
  return contents.filter((item) =>
    phase.value === "gather" ? item.verified : item.return_complete,
  ).length
}

function containerCountLabel(contents: OutingItemSummary[]) {
  const state = phase.value === "gather" ? "got it" : "ready to come in"
  return `${checkedCount(contents)} of ${contents.length} ${state}`
}

function forwardPack(item: OutingItemSummary, packed: boolean) {
  emit("pack", item, packed)
}

function forwardVerification(item: OutingItemSummary, result: VerificationResult) {
  emit("verify", item, result)
}
</script>

<template>
  <div v-if="items.length" class="flex flex-col gap-4">
    <UTabs v-model="activeView" :items="viewItems" color="neutral" variant="pill" class="w-full" />

    <template v-if="activeView === 'container'">
      <section
        v-for="group in containerGroups"
        :key="group.container.id"
        data-testid="container-group"
        class="border-default bg-muted overflow-hidden rounded-lg border"
      >
        <div class="flex flex-wrap items-center gap-2 p-4">
          <button
            type="button"
            data-testid="toggle-container"
            class="focus-visible:outline-primary order-1 flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-md text-left"
            :aria-expanded="expandedContainers.has(group.container.id)"
            @click="toggleContainer(group.container.id)"
          >
            <UAvatar :src="group.container.item_thumb_url ?? undefined" alt="" size="md" />
            <span class="min-w-0 flex-1">
              <span class="text-highlighted block truncate text-sm font-semibold">
                {{ group.container.item_name }}
              </span>
              <span class="text-muted block text-xs">
                {{ containerCountLabel(group.contents) }}
              </span>
            </span>
            <UIcon
              :name="
                expandedContainers.has(group.container.id) ? ICON.chevronDown : ICON.chevronRight
              "
              class="text-muted size-5 shrink-0"
            />
          </button>

          <UButton
            v-if="outingStatus === 'draft'"
            data-testid="pack-container"
            :label="group.container.packed ? 'Loaded' : 'Ready to go out'"
            :icon="group.container.packed ? ICON.success : ICON.outing"
            :disabled="!group.container.verified"
            :color="group.container.packed ? 'success' : 'neutral'"
            :variant="group.container.packed ? 'soft' : 'solid'"
            size="sm"
            class="order-3 min-h-11 w-full justify-center sm:order-none sm:w-auto"
            :loading="processingItemId === group.container.id"
            @click="emit('pack', group.container, !group.container.packed)"
          />
          <UBadge
            v-else
            :label="group.container.return_complete ? 'Ready to come in' : 'Return check'"
            :color="group.container.return_complete ? 'success' : 'neutral'"
            variant="subtle"
            class="order-3 w-full justify-center sm:order-none sm:w-auto"
          />

          <UButton
            v-if="outingStatus === 'draft'"
            :icon="ICON.delete"
            aria-label="Remove container"
            color="neutral"
            variant="ghost"
            square
            size="sm"
            class="order-2 min-h-11 min-w-11 sm:order-none"
            @click="emit('remove', group.container.id)"
          />
        </div>

        <p
          v-if="outingStatus === 'draft' && !group.container.verified"
          class="text-muted border-muted border-t px-4 py-2 text-xs"
        >
          Verify contents before loading.
        </p>

        <SbList
          v-if="expandedContainers.has(group.container.id) && group.contents.length"
          separator
          class="border-muted bg-default border-t"
        >
          <OutingVerificationRow
            v-for="item in group.contents"
            :key="item.id"
            :item="item"
            :phase="phase"
            :outing-status="outingStatus"
            :processing="processingItemId === item.id"
            :show-pack="false"
            @check-in="emit('checkIn', $event)"
            @pack="forwardPack"
            @remove="emit('remove', $event)"
            @verify="forwardVerification"
          />
        </SbList>

        <p
          v-else-if="expandedContainers.has(group.container.id)"
          class="text-muted border-muted bg-default border-t p-4 text-sm"
        >
          No contents on this outing.
        </p>
      </section>

      <section v-if="looseItems.length" class="flex flex-col gap-2">
        <h3 class="text-highlighted text-sm font-semibold">Loose items</h3>
        <SbList bordered separator>
          <OutingVerificationRow
            v-for="item in looseItems"
            :key="item.id"
            :item="item"
            :phase="phase"
            :outing-status="outingStatus"
            :processing="processingItemId === item.id"
            :show-pack="true"
            show-remove
            @check-in="emit('checkIn', $event)"
            @pack="forwardPack"
            @remove="emit('remove', $event)"
            @verify="forwardVerification"
          />
        </SbList>
      </section>
    </template>

    <SbList v-else bordered separator>
      <OutingVerificationRow
        v-for="item in itemRows"
        :key="item.id"
        data-testid="item-row"
        :item="item"
        :phase="phase"
        :outing-status="outingStatus"
        :processing="processingItemId === item.id"
        :show-pack="item.home_container_id === null"
        :show-remove="outingStatus === 'draft'"
        @check-in="emit('checkIn', $event)"
        @pack="forwardPack"
        @remove="emit('remove', $event)"
        @verify="forwardVerification"
      />
    </SbList>
  </div>

  <UEmpty
    v-else
    :icon="ICON.outing"
    title="No items packed yet"
    description="Search for an item above to add it to this outing."
  />
</template>
